const template = document.createElement("template");

template.innerHTML = `
<style>
  :host{
    position: relative;
    display: inline-block;
    font-family: var(--ctl-font-sans, system-ui, -apple-system, Segoe UI, Roboto, Arial);
    color: var(--ctl-color-text);
  }

  .backdrop{
    position: fixed;
    inset: 0;
    z-index: 999; /* One below --ctl-z-dropdown (1000) */
    display: none;
  }

  .backdrop.visible{
    display: block;
  }

  .popover{
    position: absolute;
    background: var(--ctl-color-surface);
    border: 1px solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-lg);
    box-shadow: var(--ctl-shadow-dropdown);
    padding: var(--ctl-space-4);
    max-width: 360px;
    z-index: var(--ctl-z-dropdown);
    opacity: 0;
    pointer-events: none;
    transition: opacity var(--ctl-transition-normal);
  }

  .popover.visible{
    opacity: 1;
    pointer-events: auto;
  }

  /* Positioning */
  .popover.top{
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%) translateY(calc(-1 * var(--ctl-space-2)));
  }

  .popover.bottom{
    top: 100%;
    left: 50%;
    transform: translateX(-50%) translateY(var(--ctl-space-2));
  }

  .popover.left{
    right: 100%;
    top: 50%;
    transform: translateY(-50%) translateX(calc(-1 * var(--ctl-space-2)));
  }

  .popover.right{
    left: 100%;
    top: 50%;
    transform: translateY(-50%) translateX(var(--ctl-space-2));
  }

  /* Arrow */
  .popover::before{
    content: "";
    position: absolute;
    width: 0;
    height: 0;
    border: var(--ctl-space-2) solid transparent;
  }

  .popover.top::before{
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-top-color: var(--ctl-color-surface);
  }

  .popover.bottom::before{
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-bottom-color: var(--ctl-color-surface);
  }

  .popover.left::before{
    left: 100%;
    top: 50%;
    transform: translateY(-50%);
    border-left-color: var(--ctl-color-surface);
  }

  .popover.right::before{
    right: 100%;
    top: 50%;
    transform: translateY(-50%);
    border-right-color: var(--ctl-color-surface);
  }

  .header{
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--ctl-space-3);
    margin-bottom: var(--ctl-space-3);
  }

  .header:empty{
    display: none;
    margin-bottom: 0;
  }

  .title{
    font-weight: var(--ctl-font-weight-semibold);
    font-size: var(--ctl-font-size-lg);
  }

  .close{
    all: unset;
    cursor: pointer;
    width: var(--ctl-size-icon-lg);
    height: var(--ctl-size-icon-lg);
    border-radius: var(--ctl-radius-sm);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--ctl-color-muted);
    transition: background var(--ctl-transition-fast), color var(--ctl-transition-fast);
    flex: 0 0 auto;
  }

  .close:hover{
    background: color-mix(in srgb, var(--ctl-color-text) 6%, transparent);
    color: var(--ctl-color-text);
  }

  .close:focus-visible{
    outline: var(--ctl-focus-ring-width) solid var(--ctl-color-focus);
    outline-offset: var(--ctl-focus-ring-offset);
  }

  .content{
    font-size: var(--ctl-font-size-base);
    line-height: var(--ctl-leading-normal);
  }

  .footer{
    margin-top: var(--ctl-space-3);
    display: flex;
    gap: var(--ctl-space-2);
    justify-content: flex-end;
  }

  .footer:empty{
    display: none;
    margin-top: 0;
  }
</style>

<div class="backdrop" part="backdrop" id="backdrop"></div>

<span class="trigger" part="trigger" id="trigger">
  <slot name="trigger"></slot>
</span>

<div class="popover" part="popover" id="popover" role="dialog" aria-modal="false">
  <div class="header" part="header">
    <div class="title" part="title" id="title">
      <slot name="title"></slot>
    </div>
    <button class="close" part="close" id="close" type="button" aria-label="Close" title="Close">
      ×
    </button>
  </div>

  <div class="content" part="content">
    <slot></slot>
  </div>

  <div class="footer" part="footer">
    <slot name="footer"></slot>
  </div>
</div>
`;

export class CtlPopover extends HTMLElement {
  static get observedAttributes() {
    return ["open", "placement", "trigger"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));

    this._backdrop = this.shadowRoot.getElementById("backdrop");
    this._trigger = this.shadowRoot.getElementById("trigger");
    this._popover = this.shadowRoot.getElementById("popover");
    this._close = this.shadowRoot.getElementById("close");
    this._titleSlot = this.shadowRoot.querySelector('slot[name="title"]');

    this._onClick = this._onClick.bind(this);
    this._onBackdropClick = this._onBackdropClick.bind(this);
    this._onCloseClick = this._onCloseClick.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onTitleSlotChange = this._onTitleSlotChange.bind(this);
  }

  connectedCallback() {
    this._syncPlacement();
    this._syncOpen();

    const triggerMode = this.getAttribute("trigger") || "click";

    if (triggerMode === "click") {
      this._trigger.addEventListener("click", this._onClick);
    }

    this._backdrop.addEventListener("click", this._onBackdropClick);
    this._close.addEventListener("click", this._onCloseClick);
    document.addEventListener("keydown", this._onKeyDown);

    // Hide close button if no title
    this._titleSlot.addEventListener("slotchange", this._onTitleSlotChange);
  }

  disconnectedCallback() {
    this._trigger.removeEventListener("click", this._onClick);
    this._backdrop.removeEventListener("click", this._onBackdropClick);
    this._close.removeEventListener("click", this._onCloseClick);
    this._titleSlot.removeEventListener("slotchange", this._onTitleSlotChange);
    document.removeEventListener("keydown", this._onKeyDown);
  }

  attributeChangedCallback(name) {
    if (name === "placement") this._syncPlacement();
    if (name === "open") this._syncOpen();
  }

  // --- Public API ---
  show() {
    if (this.hasAttribute("open")) return;
    this.setAttribute("open", "");
  }

  hide() {
    if (!this.hasAttribute("open")) return;
    this.removeAttribute("open");
  }

  toggle() {
    this.hasAttribute("open") ? this.hide() : this.show();
  }

  // --- Internal ---
  _syncPlacement() {
    const placement = this.getAttribute("placement") || "bottom";
    const validPlacements = ["top", "bottom", "left", "right"];

    // Remove all placement classes
    validPlacements.forEach((p) => this._popover.classList.remove(p));

    // Add current placement
    if (validPlacements.includes(placement)) {
      this._popover.classList.add(placement);
    } else {
      this._popover.classList.add("bottom");
    }
  }

  _syncOpen() {
    const isOpen = this.hasAttribute("open");

    if (isOpen) {
      this._backdrop.classList.add("visible");
      this._popover.classList.add("visible");

      this.dispatchEvent(
        new CustomEvent("ctl-popover-open", { bubbles: true, composed: true })
      );
    } else {
      this._backdrop.classList.remove("visible");
      this._popover.classList.remove("visible");

      this.dispatchEvent(
        new CustomEvent("ctl-popover-close", { bubbles: true, composed: true })
      );
    }
  }

  _onClick(e) {
    e.stopPropagation();
    this.toggle();
  }

  _onBackdropClick() {
    this.hide();
  }

  _onCloseClick() {
    this.hide();
  }

  _onKeyDown(e) {
    if (e.key === "Escape" && this.hasAttribute("open")) {
      e.preventDefault();
      this.hide();
    }
  }

  _onTitleSlotChange() {
    const hasTitle = this._titleSlot.assignedElements().length > 0;
    this._close.style.display = hasTitle ? "" : "none";
  }
}

customElements.define("ctl-popover", CtlPopover);
